import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import SearchableCountrySelect from '../components/SearchableCountrySelect';
import { adminCountriesAPI, adminLocationsAPI } from '../services/api';

const emptyForm = () => ({
  name: '',
  name_ar: '',
  city: '',
  country_id: '',
});

const LocationFormPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(locationId);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState(emptyForm());
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cRes = await adminCountriesAPI.getAll();
        if (!cancelled) setCountries(cRes.data?.countries || []);
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isEdit) {
      setFormData(emptyForm());
      setLoading(false);
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const locRes = await adminLocationsAPI.getById(locationId);
        const loc = locRes.data?.location;
        if (!cancelled) {
          if (loc) {
            setFormData({
              name: loc.name || '',
              name_ar: loc.name_ar || '',
              city: loc.city || '',
              country_id: loc.country_id || loc.country?.id || '',
            });
          } else {
            toast.error('Location not found');
          }
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error('Location name is required!');
      return;
    }
    if (!formData.country_id) {
      toast.error('Please select a country.');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        name_ar: formData.name_ar?.trim() || null,
        city: formData.city?.trim() || null,
        country_id: formData.country_id,
      };

      if (isEdit) {
        await adminLocationsAPI.update(locationId, payload);
        toast.success('Location updated successfully!');
      } else {
        await adminLocationsAPI.create({ ...payload, is_active: true, sort_order: 0 });
        toast.success('Location created successfully!');
      }
      navigate('/locations');
    } catch (error) {
      const action = isEdit ? 'updating' : 'creating';
      let errorMessage = `Error ${action} location: `;
      if (error.response?.data?.errors) {
        errorMessage += '\n' + Object.values(error.response.data.errors).flat().join('\n');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      toast.error(errorMessage);
    }
  };

  const title = isEdit ? 'Edit location' : 'Add location';

  return (
    <AdminSubpage title={title} backTo="/locations">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Arabic Name</label>
              <input
                type="text"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Country *</label>
              <SearchableCountrySelect
                countries={countries}
                value={formData.country_id}
                onChange={(id) => setFormData({ ...formData, country_id: id })}
                placeholder="Search country…"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Update Location' : 'Create Location'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/locations')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default LocationFormPage;
